import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Group,
  NumberInput,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import IconAlertCircle from "@tabler/icons-react/dist/esm/icons/IconAlertCircle.mjs";
import IconCalendarWeek from "@tabler/icons-react/dist/esm/icons/IconCalendarWeek.mjs";
import IconSearch from "@tabler/icons-react/dist/esm/icons/IconSearch.mjs";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  clearManualSearch,
  setManualSearchParams,
  useSelectManualSearchEnabled,
} from "../../../redux/features/manualSearch/manualSearchSlice";
import { useManualSearchFormData } from "./useManualSearchForm.logic";

function getFieldLabel(t, searchField) {
  const tableFieldName = searchField?.field?.tableFieldName;
  if (!tableFieldName) return searchField?.name || "";
  return t(`manualSearch.${tableFieldName.toLowerCase()}`);
}

function normalizeDate(value) {
  if (!value) return "";
  return value instanceof Date ? value.toISOString() : value;
}

export default function ManualSearchForm() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [filterValue, setFilterValue] = useState([]);
  const [formKey, setFormKey] = useState(0);
  const enabled = useSelectManualSearchEnabled();

  const { searchFieldsData, isLoading, isError } = useManualSearchFormData();

  function setFilter(fieldId, value) {
    setFilterValue((currentFilters) => {
      const nextFilters = [...currentFilters];
      const filterIndex = nextFilters.findIndex((f) => f.fieldId === fieldId);
      const isEmpty = value === "" || value === null || value === undefined;

      if (isEmpty) {
        if (filterIndex !== -1) nextFilters.splice(filterIndex, 1);
        return nextFilters;
      }

      const nextFilter = { fieldId, value };
      if (filterIndex === -1) nextFilters.push(nextFilter);
      else nextFilters[filterIndex] = nextFilter;

      return nextFilters;
    });
  }

  function hasFilterData() {
    return filterValue.some((f) => f.value !== "");
  }

  function handleClear() {
    setFilterValue([]);
    setFormKey((k) => k + 1);
    dispatch(clearManualSearch());
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!hasFilterData()) return;

    dispatch(
      setManualSearchParams({
        filters: filterValue,
        searchId: searchFieldsData?.id,
        ruleId: searchFieldsData?.baseSearch?.rule?.id,
        columns: searchFieldsData?.columnsNames || [],
      }),
    );
  }

  if (isError) {
    return (
      <Alert color="red" icon={<IconAlertCircle size={18} />} variant="light">
        Something happened. Try again later.
      </Alert>
    );
  }

  const isDirty = filterValue.length > 0 || enabled;

  const inputStyles = {
    root: { position: "relative", paddingBottom: 20 },
    error: { position: "absolute", bottom: 0, whiteSpace: "nowrap" },
  };

  return (
    <Box bg="gray.3" p="md" style={{ borderBottom: "1px solid #d0d0d0" }}>
      <form onSubmit={handleSubmit}>
        <Group key={formKey} align="center" gap="sm" wrap="wrap">
          {searchFieldsData?.fields?.map((searchField) => {
            if (!searchField?.field) return null;

            const fieldId = searchField.id;
            const label = getFieldLabel(t, searchField);
            const isUic = searchField.name?.toLowerCase() === "uic";

            if (searchField.field.type === "DATE") {
              return (
                <DateInput
                  key={fieldId}
                  w={270}
                  clearable
                  disabled={isLoading}
                  label={label}
                  rightSection={<IconCalendarWeek size={18} />}
                  valueFormat="DD.MM.YYYY"
                  styles={inputStyles}
                  onChange={(value) => setFilter(fieldId, normalizeDate(value))}
                />
              );
            }

            if (searchField.field.type === "NUMBER" && !isUic) {
              return (
                <NumberInput
                  key={fieldId}
                  w={270}
                  allowDecimal={false}
                  disabled={isLoading}
                  hideControls
                  label={label}
                  styles={inputStyles}
                  onChange={(value) => setFilter(fieldId, value)}
                />
              );
            }

            return (
              <TextInput
                key={fieldId}
                w={270}
                disabled={isLoading}
                inputMode={isUic ? "numeric" : undefined}
                label={label}
                styles={inputStyles}
                onChange={(event) => {
                  setFilter(fieldId, event.currentTarget.value);
                }}
              />
            );
          })}

          <Button
            disabled={!isDirty}
            variant="outline"
            color="black"
            onClick={handleClear}
            type="button"
          >
            Clear
          </Button>

          <Button
            disabled={!hasFilterData()}
            leftSection={<IconSearch size={18} />}
            loading={isLoading}
            type="submit"
          >
            Search
          </Button>
        </Group>
      </form>
    </Box>
  );
}
